<script setup>
import { computed, ref } from 'vue';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { useRouter, useRoute } from 'vue-router';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper.js';
import { shortTimestamp, dynamicTime } from 'shared/helpers/timeHelper';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import CardMessagePreview from './CardMessagePreview.vue';
import CardMessagePreviewWithMeta from './CardMessagePreviewWithMeta.vue';
import CardPriorityIcon from './CardPriorityIcon.vue';

import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useStore } from 'vuex';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  contact: {
    type: Object,
    required: true,
  },
  stateInbox: {
    type: Object,
    required: true,
  },
  accountLabels: {
    type: Array,
    required: true,
  },
});

const router = useRouter();
const route = useRoute();

const cardMessagePreviewWithMetaRef = ref(null);

const currentContact = computed(() => props.contact);

const currentContactName = computed(() => currentContact.value?.name);
const currentContactThumbnail = computed(() => currentContact.value?.thumbnail);
const currentContactStatus = computed(
  () => currentContact.value?.availabilityStatus
);

const inbox = computed(() => props.stateInbox);

const inboxName = computed(() => inbox.value?.name);

const inboxIcon = computed(() => {
  const { channelType, medium } = inbox.value;
  return getInboxIconByType(channelType, medium);
});

const lastActivityAt = computed(() => {
  const timestamp = props.conversation?.timestamp;
  return timestamp ? shortTimestamp(dynamicTime(timestamp)) : '';
});

const showMessagePreviewWithoutMeta = computed(() => {
  const { labels = [] } = props.conversation;
  return (
    !cardMessagePreviewWithMetaRef.value?.hasSlaThreshold && labels.length === 0
  );
});

const store = useStore();
const isBranchManagementEnabled = computed(() => {
  return store.getters['accounts/isFeatureEnabledonAccount'](
    route.params.accountId,
    FEATURE_FLAGS.BRANCH_MANAGEMENT
  );
});

const branchName = computed(
  () => props.conversation?.meta?.branch?.name || null
);
const showBranchName = computed(
  () => isBranchManagementEnabled.value && branchName.value != null
);

const isBranchless = computed(() => {
  return !props.conversation?.meta?.branch && !props.conversation?.branch_id;
});

const showBranchlessIndicator = computed(
  () => isBranchManagementEnabled.value && isBranchless.value
);

const onCardClick = e => {
  const path = frontendURL(
    conversationUrl({
      accountId: route.params.accountId,
      id: props.conversation.id,
    })
  );

  if (e.metaKey || e.ctrlKey) {
    window.open(
      window.chatwootConfig.hostURL + path,
      '_blank',
      'noopener noreferrer nofollow'
    );
    return;
  }
  router.push({ path });
};
</script>

<template>
  <div
    role="button"
    class="flex w-full gap-3 px-3 py-4 transition-all duration-300 ease-in-out cursor-pointer"
    @click="onCardClick"
  >
    <Avatar
      :name="currentContactName"
      :src="currentContactThumbnail"
      :size="24"
      :status="currentContactStatus"
      rounded-full
    />
    <div class="flex flex-col w-full gap-1 min-w-0">
      <div class="flex items-center justify-between h-6 gap-2">
        <h4 class="text-base font-medium truncate text-n-slate-12">
          {{ currentContactName }}
        </h4>
        <div class="flex items-center gap-2">
          <div
            v-if="showBranchlessIndicator"
            v-tooltip.left="$t('SIDEBAR.BRANCH_SELECTOR.GLOBAL')"
            class="flex items-center justify-center flex-shrink-0 px-1.5 h-5 rounded bg-n-red-2 border border-n-red-4"
          >
            <span
              class="text-[10px] font-semibold text-n-red-11 uppercase tracking-wide"
            >
              {{ $t('SIDEBAR.BRANCH_SELECTOR.GLOBAL') }}
            </span>
          </div>
          <div
            v-else-if="showBranchName"
            v-tooltip.left="branchName"
            class="flex items-center justify-center flex-shrink-0 px-1.5 h-5 rounded bg-n-slate-2 border border-n-slate-4"
          >
            <span
              class="text-[10px] font-semibold text-n-slate-11 uppercase tracking-wide max-w-[80px] truncate"
            >
              {{ branchName }}
            </span>
          </div>
          <CardPriorityIcon :priority="conversation.priority || null" />
          <div
            v-tooltip.left="inboxName"
            class="flex items-center justify-center flex-shrink-0 rounded-full bg-n-alpha-2 size-5"
          >
            <Icon
              :icon="inboxIcon"
              class="flex-shrink-0 text-n-slate-11 size-3"
            />
          </div>
          <span class="text-sm text-n-slate-10">
            {{ lastActivityAt }}
          </span>
        </div>
      </div>
      <CardMessagePreview
        v-show="showMessagePreviewWithoutMeta"
        :conversation="conversation"
      />
      <CardMessagePreviewWithMeta
        v-show="!showMessagePreviewWithoutMeta"
        ref="cardMessagePreviewWithMetaRef"
        :conversation="conversation"
        :account-labels="accountLabels"
      />
    </div>
  </div>
</template>
